import { MainNav } from '@/globalNav/a11yFunctions';
import { clearMenuFilter, setupMenuFilter } from '@/globalNav/menuFilter';
import { MenuUtils } from '@/globalNav/menuUtils';
import { FocusTrap } from '@/utils/focustrap';

class GlobalNav {
  constructor(globalNavElement) {
    //privacyOptionsBannerHeight is the height of the cookie button that displays
    //when the user has closed the cookie banner notice. If the cookie banner is open,
    //cookieBannerHeight will be updated to the actual height of the banner.
    this.privacyOptionsBannerHeight = 55;
    this.cookieBannerHeight = this.privacyOptionsBannerHeight;
    this.globalNavElement = globalNavElement;
    this.isCookieBannerVisible = isCookieBannerVisible();

    // this event fires when the cookie banner is closed
    window.addEventListener('OneTrustGroupsUpdated', () => {
      if (isCookieBannerVisible() === false) {
        this.handleCookieBannerClosed();
      }
    });

    this.mainNavOffCanvas = globalNavElement.querySelector('#main-nav');
    this.menuButton = globalNavElement.querySelector('.navbar-toggle');
    this.backdrop = globalNavElement.querySelector('.menu-page-backdrop');
    this.searchOffcanvasButton = globalNavElement.querySelector('.search-offcanvas-button');
    this.searchOffcanvasContainer = globalNavElement.querySelector('#search-offcanvas');
    this.searchComponent = globalNavElement.querySelector('mhecomm-search-v3');

    // clone the search component and append to offcanvas body
    if (this.searchComponent) {
      const clonedSearchComponent = this.searchComponent.cloneNode(true);
      clonedSearchComponent.renderSmallUI = true;
      this.searchOffcanvasContainer?.querySelector('.offcanvas-body')?.appendChild(clonedSearchComponent);
    }

    // Region selector elements
    this.regionSelectorButton = globalNavElement.querySelector('#region-selector-btn');
    this.regionSelectorOffCanvas = globalNavElement.querySelector('#region-selector');

    this.mainMenuOffcanvasBs = window?.bootstrap?.Offcanvas.getInstance(this.mainNavOffCanvas);
    this.regionSelectorOffcanvasBs = this.regionSelectorOffCanvas
      ? window?.bootstrap?.Offcanvas.getInstance(this.regionSelectorOffCanvas)
      : null;

    this.megaMenu = new MainNav(this.mainNavOffCanvas);
    this.level2Menus = Object.values(this.megaMenu.menus).filter((menu) => menu.menuType === 'level2Menu');
    this.level3Menus = Object.values(this.megaMenu.menus).filter((menu) => menu.menuType === 'level3Menu');

    if (this.isCookieBannerVisible) {
      this.globalNavElement.classList.add('cookie-banner-showing');
      this.cookieBanner = document.getElementById('onetrust-banner-sdk');
      if (this.cookieBanner) {
        this.cookieBannerHeight = Math.ceil(this.cookieBanner.getBoundingClientRect().height);
        this.globalNavElement.style.setProperty('--cookie-banner-height', `${this.cookieBannerHeight}px`);
      }
    }

    // Store focus traps for region selector level3 menus
    this.regionLevel3FocusTraps = new Map();

    this.hideSearchOffcanvasButton();
    this.initializeEventListeners();
    this.initializeRegionSelector();
    this.initializeGlobalBanner();
    this.setupZoomResponsiveFocusListener();
  }

  initializeEventListeners() {
    // Main navigation offcanvas events
    MenuUtils.setupOffcanvasListeners(this.mainNavOffCanvas, {
      onShow: this.handleMainNavShow.bind(this),
      onHide: this.handleMainNavHide.bind(this),
      onHidden: () => this.menuButton?.focus(),
    });

    // Setup menu-specific event listeners
    Object.keys(this.megaMenu.menus).forEach((menuKey) => {
      const menuObj = this.megaMenu.menus[menuKey];
      this.setupMenuTypeListeners(menuObj);
    });

    // Backdrop click handler
    this.backdrop?.addEventListener('click', this.handleBackdropClick.bind(this));
  }

  setupMenuTypeListeners(menuObj) {
    switch (menuObj.menuType) {
      case 'main-menu':
        this.setupMainMenuListeners(menuObj);
        break;
      case 'level2Menu':
        this.setupLevel2MenuListeners(menuObj);
        break;
      case 'level3Menu':
        this.setupLevel3MenuListeners(menuObj);
        break;
    }
  }

  setupMainMenuListeners(menuObj) {
    MenuUtils.setupMenuButtons(menuObj.menuElement, {
      onClose: this.handleCloseBtnClick.bind(this),
    });

    // Setup dropdown buttons
    menuObj.navItems.forEach((item) => {
      if (item.element.tagName === 'BUTTON' && !item.element.classList.contains('menu-close-btn')) {
        item.element.addEventListener('click', () => this.handleDropDownButtonClick(item.element));
      }
    });
  }

  setupLevel2MenuListeners(menuObj) {
    MenuUtils.setupMenuButtons(menuObj.menuElement, {
      onClose: () => {
        this.handleLevel2CloseBtnClick(menuObj).then(() => {
          // Original behavior: specific visible class removal for Level2
          menuObj.menuElement.classList.remove('visible');
        });
      },
      onBack: (e) => this.handleBackButtonClick(e, menuObj),
    });

    MenuUtils.setupKeyboardNav(menuObj.menuElement, () => {
      this.handleLevel2CloseBtnClick(menuObj).then(() => {
        // Original behavior: Escape key also had this cleanup
        menuObj.menuElement.classList.remove('visible');
      });
    });

    // Setup submenu buttons
    menuObj.navItems.forEach((item) => {
      if (
        item.element.tagName === 'BUTTON' &&
        !item.element.classList.contains('menu-close-btn') &&
        !item.element.classList.contains('menu-back-btn') &&
        !item.element.classList.contains('menu-cta-btn')
      ) {
        item.element.addEventListener('click', async () => {
          const targetMenu = this.megaMenu.menus[item.controls]?.menuElement;
          if (!targetMenu) return;
          await this.handleLevel2MenuButtonClick(item.element, targetMenu, menuObj);
        });
      }
    });
  }

  setupLevel3MenuListeners(menuObj) {
    setupMenuFilter(this.globalNavElement, menuObj.menuElement);

    MenuUtils.setupMenuButtons(menuObj.menuElement, {
      onClose: () => this.handleLevel3CloseBtnClick(menuObj),
      onBack: (e) => this.handleBackButtonClick(e, menuObj),
    });

    MenuUtils.setupKeyboardNav(menuObj.menuElement, () => {
      this.handleLevel3CloseBtnClick(menuObj);
    });
  }

  hideSearchOffcanvasButton() {
    // if its search page (i.e url contain search.html or /search) hide the search button from global nav
    if (window.location.href.includes('/search.html') || window.location.href.includes('/search')) {
      if (this.searchOffcanvasButton) {
        this.searchOffcanvasButton.style.setProperty('display', 'none', 'important');
      }
    }
  }

  handleBackdropClick() {
    this.hideSubMenus();
    this.hideRegionLevel3Menus();
    this.hideMenuBackdrop();
    MenuUtils.toggleBodyScroll(false);
    // Close region selector if open
    if (this.regionSelectorOffcanvasBs && this.regionSelectorOffCanvas.classList.contains('show')) {
      this.handleRegionSelectorClose();
    }
  }

  showMenuBackdrop() {
    MenuUtils.backdrop.show(this.backdrop);
  }

  hideMenuBackdrop() {
    MenuUtils.backdrop.hide(this.backdrop);
  }

  handleMainNavShow() {
    this.mainMenuOffcanvasBs = window.bootstrap.Offcanvas.getInstance(this.mainNavOffCanvas);
    this.mainNavOffCanvas?.setAttribute('aria-labelledby', 'mainNavPanelLabel');
    // this.backdrop.classList.add('show');
  }
  handleMainNavHide() {
    this.hideSubMenus();
    this.mainNavOffCanvas?.removeAttribute('aria-labelledby');
    // Remove overflow hidden styling from body when offcanvas is hidden
    MenuUtils.toggleBodyScroll(false);
    // this.backdrop.classList.remove('show');
  }

  handleCloseBtnClick() {
    if (this.mainMenuOffcanvasBs) {
      this.mainMenuOffcanvasBs.hide();
    }
    this.hideMenuBackdrop();
    MenuUtils.toggleBodyScroll(false);
    Object.keys(this.megaMenu?.menus ?? {}).forEach((menuId) => {
      this.megaMenu.menus[menuId].resetMenu();
    });
  }

  async handleLevel3CloseBtnClick(menu) {
    this.hideSubMenus();
    this.handleCloseBtnClick();
    await this.delay(200);
    this.focusMainNavTrigger(menu.menuId.split('-')[0]);
  }

  async handleLevel2CloseBtnClick(menu) {
    this.hideSubMenus();
    this.handleCloseBtnClick();
    menu.focusTrap.deactivate();
    await this.delay(200);
    this.focusMainNavTrigger(menu.menuId);
  }

  focusMainNavTrigger(controls) {
    this.megaMenu.menus['main-nav']?.navItems.forEach((item) => {
      if (item.controls === controls) {
        item.element.focus();
      }
    });
  }

  async handleBackButtonClick(e, menuObj) {
    const menu = menuObj.menuElement;
    const parentMenuId = menuObj.menuType === 'level2Menu' ? 'main-nav' : e.target.getAttribute('data-parent-menu-id');
    const parentMenu = this.megaMenu.menus[parentMenuId];
    const parentBtn = parentMenu?.navItems.find((item) => item.controls === menu.id)?.element;

    if (!parentMenu || !parentBtn) return;

    menu.classList.remove('show');
    menuObj.resetMenu();
    await this.delay(200);
    menu.classList.remove('visible');
    if (menuObj.menuType === 'level3Menu') {
      menu.classList.add('d-none');
    }
    parentBtn.focus();
    if (parentMenu.menuType === 'level2Menu') {
      parentMenu.ulElement.classList.add('overflow-scroll');
    }
  }

  handleDropDownButtonClick(btn) {
    if (window.micrositeNav) {
      window.micrositeNav.resetAllMicrositeMenus();
    }

    if (this.isCookieBannerVisible && !this.cookieBanner) {
      this.cookieBanner = document.getElementById('onetrust-banner-sdk');
      if (this.cookieBanner) {
        this.cookieBannerHeight = Math.ceil(this.cookieBanner.getBoundingClientRect().height);
        this.globalNavElement.style.setProperty('--cookie-banner-height', `${this.cookieBannerHeight}px`);
      }
    }
    this.level2Menus.forEach((menu) => {
      menu.ulElement.classList.add('overflow-scroll');
      menu.ulElement.style.height = `${
        window.innerHeight - menu.ulElement.getBoundingClientRect().top - this.cookieBannerHeight
      }px`;
    });
    const menu = btn.nextElementSibling;
    if (!menu.classList.contains('show')) {
      this.showMenuBackdrop();
      MenuUtils.toggleBodyScroll(true);
      MenuUtils.toggleMenu(menu, true);
      menu.querySelector('.menu-close-btn')?.focus();
      this.megaMenu.menus[menu.id]?.focusTrap?.activate();
    }
  }

  hideAllLevel3Menus() {
    this.level3Menus.forEach((menu) => {
      if (menu.menuElement.classList.contains('show')) {
        MenuUtils.toggleLevel3Menu(menu.menuElement, false);
        MenuUtils.setAriaExpanded(menu.menuElement.previousElementSibling, false);
        clearMenuFilter(menu.menuElement, this.globalNavElement);
        menu.resetMenu();
        menu.menuElement.classList.add('d-none');
      }
    });
  }

  async handleLevel2MenuButtonClick(menuButton, menu, menuObj) {
    if (!menu.classList.contains('show')) {
      menuObj.ulElement.classList.remove('overflow-scroll');
      menu.classList.remove('d-none');
      menu.style.top = `-${menuButton.parentElement.getBoundingClientRect().top}px`;
      // This little bit keeps the menu from getting cut off at the bottom of the screen
      const menuDefinition = this.megaMenu.menus[menu.id];
      if (!menuDefinition) return;
      const menuUl = menuDefinition.ulElement;
      if (!menuUl) return;
      menuUl.style.height = `${window.innerHeight - menuUl.getBoundingClientRect().top - this.cookieBannerHeight}px`;
      MenuUtils.toggleMenu(menu, true);
      menuObj.resetMenu();
      menuDefinition.focusTrap?.activate();
      const firstItem = menuDefinition.navItems[menuDefinition.firstFocusableElement]?.element;
      firstItem?.focus();
    }
  }

  hideSubMenus() {
    this.hideAllLevel3Menus();
    this.level2Menus.forEach((menu) => {
      menu.menuElement.classList.remove('show');
      MenuUtils.setAriaExpanded(menu.menuElement.previousElementSibling, false);
      menu.navItems.forEach((item) => item.element.classList.remove('active'));
      menu.resetMenu();
      setTimeout(() => {
        menu.menuElement.classList.remove('visible');
      }, 200);
    });
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  initializeRegionSelector() {
    if (!this.regionSelectorOffCanvas) return;

    // Setup offcanvas event listeners
    MenuUtils.setupOffcanvasListeners(this.regionSelectorOffCanvas, {
      onShow: this.handleRegionSelectorShow.bind(this),
      onHide: this.handleRegionSelectorHide.bind(this),
      onHidden: () => this.regionSelectorButton.focus(),
    });

    // Setup menu buttons (close and back)
    MenuUtils.setupMenuButtons(this.regionSelectorOffCanvas, {
      onClose: this.handleRegionSelectorClose.bind(this),
      onBack: this.handleRegionSelectorClose.bind(this),
    });

    // Setup submenu buttons
    const submenuButtons = this.regionSelectorOffCanvas.querySelectorAll('.open-submenu');
    submenuButtons.forEach((button) => {
      button.addEventListener('click', () => this.handleRegionSubmenuOpen(button));
    });

    // Setup level 3 menus
    const level3Menus = this.regionSelectorOffCanvas.querySelectorAll('.level3Menu');
    level3Menus.forEach((level3Menu) => {
      this.initializeRegionLevel3Menu(level3Menu);
    });

    // Setup keyboard navigation
    MenuUtils.setupKeyboardNav(this.regionSelectorOffCanvas, () => {
      this.handleRegionSelectorClose();
    });
  }

  initializeRegionLevel3Menu(level3Menu) {
    // Create focus trap for this menu
    const focusTrap = new FocusTrap({
      trapElement: level3Menu,
      autofocus: false,
    });
    this.regionLevel3FocusTraps.set(level3Menu.id, focusTrap);

    // Setup menu buttons
    MenuUtils.setupMenuButtons(level3Menu, {
      onClose: () => this.handleRegionLevel3Close(level3Menu),
      onBack: () => this.handleRegionLevel3Back(level3Menu),
    });

    // Setup keyboard navigation
    MenuUtils.setupKeyboardNav(level3Menu, () => {
      this.handleRegionLevel3Close(level3Menu);
    });
  }

  handleRegionSelectorShow() {
    this.regionSelectorOffcanvasBs = window.bootstrap.Offcanvas.getInstance(this.regionSelectorOffCanvas);
    MenuUtils.toggleBodyScroll(true);
  }

  handleRegionSelectorHide() {
    this.hideRegionLevel3Menus();
    MenuUtils.toggleBodyScroll(false);
  }

  handleRegionSelectorClose() {
    this.hideRegionLevel3Menus();
    MenuUtils.toggleBodyScroll(false);
  }

  handleRegionSubmenuOpen(button) {
    const targetMenuId = button.getAttribute('aria-controls');
    const targetMenu = this.regionSelectorOffCanvas.querySelector(`#${targetMenuId}`);

    if (targetMenu && !targetMenu.classList.contains('show')) {
      // Hide other level 3 menus
      this.hideRegionLevel3Menus();

      // Show target menu - use region-specific version (no d-none)
      MenuUtils.toggleRegionLevel3Menu(targetMenu, true);

      // Activate focus trap for this menu
      const focusTrap = this.regionLevel3FocusTraps.get(targetMenu.id);
      if (focusTrap) {
        focusTrap.activate();
      }

      // Focus on close button
      const closeBtn = targetMenu.querySelector('.menu-close-btn');
      if (closeBtn) closeBtn.focus();

      // Update aria-expanded
      MenuUtils.setAriaExpanded(button, true);
    }
  }

  handleRegionLevel3Close(level3Menu) {
    // Deactivate focus trap
    const focusTrap = this.regionLevel3FocusTraps.get(level3Menu.id);
    if (focusTrap) {
      focusTrap.deactivate();
    }

    MenuUtils.toggleRegionLevel3Menu(level3Menu, false);
    MenuUtils.handleMenuClose(this.regionSelectorOffCanvas, level3Menu.id);
    this.handleRegionSelectorClose();
  }

  handleRegionLevel3Back(level3Menu) {
    // Deactivate focus trap
    const focusTrap = this.regionLevel3FocusTraps.get(level3Menu.id);
    if (focusTrap) {
      focusTrap.deactivate();
    }

    MenuUtils.toggleRegionLevel3Menu(level3Menu, false);
    MenuUtils.handleMenuClose(this.regionSelectorOffCanvas, level3Menu.id);
  }

  hideRegionLevel3Menus() {
    MenuUtils.hideMenus(this.regionSelectorOffCanvas, '.level3Menu', (menu) => {
      // Deactivate focus trap for this menu
      const focusTrap = this.regionLevel3FocusTraps.get(menu.id);
      if (focusTrap) {
        focusTrap.deactivate();
      }

      MenuUtils.handleMenuClose(this.regionSelectorOffCanvas, menu.id, false);
    });
  }

  initializeGlobalBanner() {
    const alertElement = document.getElementById('global-banner');
    if (alertElement) {
      const contentElement = alertElement.querySelector('.global-banner-content');
      if (contentElement && contentElement.id) {
        const currentBannerId = contentElement.id;
        const dismissedBannerId = localStorage.getItem('globalBannerDismissedId');
        if (dismissedBannerId !== currentBannerId) {
          alertElement.classList.remove('d-none');
          alertElement.classList.add('show');
          this.addAlertListener(alertElement, currentBannerId);
        }
      }
    }
  }

  addAlertListener(alertElement, bannerId) {
    if (alertElement) {
      alertElement.addEventListener('closed.bs.alert', () => {
        localStorage.setItem('globalBannerDismissedId', bannerId);
      });
    }
  }

  // the purpose of this function is to add focus listener for 400% zoom scenario
  // which will scroll level2/level3 menu into view when they receive focus
  setupZoomResponsiveFocusListener() {
    const mediaQuery = window.matchMedia('(min-resolution: 4dppx)');
    let focusListener = null;

    const setupFocusListener = (mq) => {
      if (mq.matches) {
        if (!focusListener) {
          focusListener = (e) => {
            const level3Menu = e.target.closest('.level3Menu');
            const level2Menu = e.target.closest('.level2Menu');

            if (level3Menu) {
              level3Menu.scrollIntoView({ block: 'nearest', behavior: 'auto' });
            } else if (level2Menu) {
              level2Menu.scrollIntoView({ block: 'nearest', behavior: 'auto' });
            }
          };
          document.addEventListener('focus', focusListener, true);
        }
      } else {
        if (focusListener) {
          document.removeEventListener('focus', focusListener, true);
          focusListener = null;
        }
      }
    };

    setupFocusListener(mediaQuery);
    mediaQuery.addEventListener('change', setupFocusListener);
  }

  handleCookieBannerClosed() {
    //reset cookie data back to defaults
    this.isCookieBannerVisible = false;
    this.cookieBanner = null;
    this.cookieBannerHeight = this.privacyOptionsBannerHeight;
    this.globalNavElement.classList.remove('cookie-banner-showing');
    // remove extra bottom padding from level2 menus
    this.level2Menus?.forEach((menu) => {
      menu.ulElement.style.removeProperty('padding-bottom');
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initGlobalNav();
});

function initGlobalNav() {
  window.globalNavElement = document.getElementById('global-nav');

  if (window.globalNavElement) {
    new GlobalNav(window.globalNavElement);
  }
}

function isCookieBannerVisible() {
  //if OptanonAlertBoxClosed cookie exists, return false
  return !document.cookie.split('; ').find((row) => row.startsWith('OptanonAlertBoxClosed='));
}
/** TODO this is a temp fix we will control below code behavior from aem template instead of JS */
(function () {
  const addMicrositeClass = (observer) => {
    const targetElement = document.querySelector('.micrositenav.navigation');
    if (targetElement) {
      document.body.classList.add('microsite');
      observer?.disconnect();
      return true;
    }
    return false;
  };

  if (addMicrositeClass()) return;

  document.addEventListener('DOMContentLoaded', () => {
    const mainFragment = document.querySelector('body main .experiencefragment');
    if (!mainFragment) return;

    const observer = new MutationObserver(() => addMicrositeClass(observer));
    observer.observe(mainFragment, { childList: true, subtree: true });

    addMicrositeClass(observer);
  });
})();
